// Clienteling: a customer's book of notes, wishes and follow-ups, shared by
// every store (server: getFashionClientBook, getFashionFollowUps,
// addFashionClientItem, closeFashionClientItem). A wish is a product the
// customer wants or a description of it; when the product is in stock at a
// store it shows in that store's follow-ups, next to the calls that are due.
// The server checks the same rules as validateClientDraft below.
export const KIND_LABEL = { NOTE: "Note", WISH: "Wish", TASK: "Follow-up" };
export const KINDS = ["NOTE", "WISH", "TASK"];
export const MAX_CLIENT_TEXT = 500;
export const MAX_FOLLOW_UP_DAYS = 365;

const clean = (s) => String(s ?? "").replace(/["\\]/g, "").replace(/[\r\n]+/g, " ").trim();
const str = (s) => `"${clean(s)}"`;
const FIELDS = "itemId kind customerId customerName mobileNo bunitId storeName text productId productName dueDate status outcome created closedAt onHand";
const REPLY = "{ status message recordId documentNo }";

export const buildClientBookQuery = (customerId, includeClosed = false) => ({
  query: `query { getFashionClientBook(customerId: ${str(customerId)}${includeClosed ? ", includeClosed: true" : ""}) { ${FIELDS} }}`,
});

export const buildFollowUpsQuery = (bunitId, dueBy) => ({
  query: `query { getFashionFollowUps(bunitId: ${str(bunitId)}${dueBy ? `, dueBy: ${str(dueBy)}` : ""}) { ${FIELDS} }}`,
});

export function buildAddClientItemMutation(item) {
  const fields = [
    `bunitId: ${str(item.bunitId)}`,
    `customerId: ${str(item.customerId)}`,
    `kind: ${str(item.kind)}`,
    item.text ? `text: ${str(item.text)}` : null,
    item.productId ? `productId: ${str(item.productId)}` : null,
    item.dueDate ? `dueDate: ${str(item.dueDate)}` : null,
  ].filter(Boolean);
  return { query: `mutation { addFashionClientItem(item: { ${fields.join(", ")} }) ${REPLY} }` };
}

export const buildCloseClientItemMutation = (itemId, status, outcome) => {
  const o = clean(outcome).slice(0, 255);
  return { query: `mutation { closeFashionClientItem(itemId: ${str(itemId)}, status: ${str(status)}${o ? `, outcome: ${str(o)}` : ""}) ${REPLY} }` };
};

// Server response → item rows, or null when the call failed.
export function readClientItems(response, field) {
  if (!response || response?.data?.errors?.length) return null;
  const rows = response?.data?.data?.[field];
  if (!Array.isArray(rows)) return null;
  return rows.map((r) => ({
    id: r.itemId,
    kind: r.kind || "NOTE",
    customerId: r.customerId || null,
    customerName: r.customerName || "",
    mobileNo: r.mobileNo || "",
    storeName: r.storeName || "",
    text: r.text || "",
    productId: r.productId || null,
    productName: r.productName || "",
    dueDate: r.dueDate || "",
    status: r.status || "OPEN",
    outcome: r.outcome || "",
    created: r.created || "",
    onHand: r.onHand == null || !Number.isFinite(Number(r.onHand)) ? null : Number(r.onHand),
  }));
}

const DAY_MS = 24 * 60 * 60 * 1000;
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const today = (now = new Date()) => isoDay(now);
export const lastFollowUpDay = (now = new Date()) => isoDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() + MAX_FOLLOW_UP_DAYS));

// A draft from the add form → { item } or { error }, with the server's words.
export function validateClientDraft(draft, now = new Date()) {
  const kind = draft?.kind;
  if (!KINDS.includes(kind)) return { error: "Choose a note, a wish or a follow-up." };
  const text = clean(draft.text);
  if (text.length > MAX_CLIENT_TEXT) return { error: `Keep it to ${MAX_CLIENT_TEXT} characters or fewer.` };
  const productId = kind === "WISH" ? draft.productId || null : null;
  if (kind === "WISH" && !text && !productId) return { error: "Pick the product or describe what the customer wants." };
  if (kind === "NOTE" && !text) return { error: "Write the note." };
  if (kind === "TASK" && !text) return { error: "Say what the follow-up is for." };
  let dueDate = null;
  if (kind === "TASK") {
    dueDate = String(draft.dueDate || "");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dueDate) || Number.isNaN(Date.parse(dueDate))) return { error: "Set the date to follow up." };
    if (dueDate < today(now) || dueDate > lastFollowUpDay(now)) return { error: "Follow up between today and a year from now." };
  }
  return { item: { bunitId: draft.bunitId, customerId: draft.customerId, kind, text, productId, dueDate } };
}

// How late a follow-up is: 0 today, >0 days overdue, <0 not yet due.
export function daysOverdue(dueDate, now = new Date()) {
  if (!dueDate) return null;
  const due = Date.parse(`${String(dueDate).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(due)) return null;
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((start - due) / DAY_MS);
}

export function dueLabel(dueDate, now = new Date()) {
  const d = daysOverdue(dueDate, now);
  if (d == null) return "";
  if (d === 0) return "Due today";
  if (d === 1) return "1 day overdue";
  if (d > 1) return `${d} days overdue`;
  return d === -1 ? "Due tomorrow" : `Due in ${-d} days`;
}

// A note is removed; a wish or follow-up is done when acted on.
export const canMarkDone = (item) => item?.status === "OPEN" && item.kind !== "NOTE";
export const itemTitle = (item) => (item.kind === "WISH" && item.productName ? item.productName : item.text);

// The customer screen's "Add a follow-up": a reason, when to call, and a note
// for whoever calls. Stored as one follow-up (TASK) whose text is
// "Reason · note", so the Follow-ups screen shows both without new fields.
export const FOLLOW_UP_REASONS = ["Size back in stock", "Alteration ready", "New arrivals", "Thank-you call"];
export const FOLLOW_UP_WHEN = [
  { label: "Tomorrow", days: 1 },
  { label: "In 3 days", days: 3 },
  { label: "Next week", days: 7 },
  { label: "In 2 weeks", days: 14 },
];
const REASON_SEP = " · ";

// "Next week" → the YYYY-MM-DD it falls on, or "" for an unknown choice.
export function followUpDue(when, now = new Date()) {
  const pick = FOLLOW_UP_WHEN.find((w) => w.label === when);
  if (!pick) return "";
  return isoDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() + pick.days));
}

export function followUpText(reason, note) {
  const r = clean(reason);
  const n = clean(note);
  return r && n ? `${r}${REASON_SEP}${n}` : r || n;
}

// Back from "Reason · note" to { reason, note }; text without a known reason is
// all note.
export function splitFollowUpText(text) {
  const t = String(text ?? "");
  const reason = FOLLOW_UP_REASONS.find((r) => t === r || t.startsWith(`${r}${REASON_SEP}`));
  if (!reason) return { reason: "", note: t };
  return { reason, note: t.slice(reason.length + REASON_SEP.length) };
}
